import { Dot, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { FormField, InputField, requiredString, TextareaField } from "common";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { CatalogItem, Experience } from "types";
import { object, string } from "yup";
import { api } from "../../../../api/api";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { NoTranslate } from "../../../Common/NoTranslate";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";

type EditCatalogItemProps = Pick<CatalogItem, "name" | "caption">;

const validationSchema = object({
    name: requiredString(),
    caption: string(),
});

interface EditGlobalDemographicModalProps {
    isOpen: boolean;
    catalogItem: CatalogItem;
    experiences: Experience[];
    onClose: (event: any) => void;
}

export const EditGlobalDemographicModal = ({
    isOpen,
    catalogItem,
    experiences,
    onClose,
}: EditGlobalDemographicModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent catalogItem={catalogItem} experiences={experiences} onClose={onClose} />
        </Modal>
    );
};

type ModalContentProps = Omit<EditGlobalDemographicModalProps, "isOpen">;

const ModalContent = ({ catalogItem, experiences, onClose }: ModalContentProps) => {
    const [showConfirmationView, setShowConfirmationView] = useState(false);
    const invalidate = useInvalidate();
    const overrideExperiences = getOverrideExperiences(experiences, catalogItem);

    const toggleConfirmationView = () => {
        setShowConfirmationView(!showConfirmationView);
    };

    const initialValues: EditCatalogItemProps = {
        name: catalogItem?.name ?? "",
        caption: catalogItem?.caption ?? "",
    };

    const { mutateAsync: updateDemographic, isLoading: isSaving } = useMutation(async (payload: CatalogItem) => {
        return api.put<CatalogItem>(`/catalogItems/${payload.id}`, payload);
    });

    const handleSubmit = async (values: EditCatalogItemProps) => {
        const updatedCatalogItem = { ...catalogItem, ...values };
        await updateDemographic(updatedCatalogItem);
        invalidate("catalogItems", updatedCatalogItem.id);
        onClose(updatedCatalogItem);
    };

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, dirty, validateForm }) => (
                <>
                    <Modal.Header>{showConfirmationView ? "Confirm" : "Edit Demographic"}</Modal.Header>

                    <Modal.Body>
                        {showConfirmationView ? (
                            <>
                                <p className="text-gray-darker">
                                    The changes you make will apply to
                                    {experiences.length > 0 && (
                                        <>
                                            <var data-var="productCount">
                                                {experiences.length - overrideExperiences.length}
                                            </var>{" "}
                                            products and
                                        </>
                                    )}
                                    &nbsp;any bookings associated with these products.{" "}
                                </p>

                                {overrideExperiences.length > 0 && (
                                    <OverrideExperiencesComponent overrideExperiences={overrideExperiences} />
                                )}
                            </>
                        ) : (
                            <fieldset className="space-y-6" disabled={isSaving}>
                                <FormField name="name" label="Name">
                                    <InputField isRequired placeholder="Name" />
                                </FormField>

                                <FormField name="caption" label="Caption">
                                    <TextareaField rows={5} placeholder="Caption" />
                                </FormField>
                            </fieldset>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        {showConfirmationView ? (
                            <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
                        ) : (
                            <SubmitButton
                                disabled={!dirty}
                                onClick={async () => {
                                    const errors = await validateForm();
                                    if (isEmpty(errors)) {
                                        toggleConfirmationView();
                                    } else {
                                        handleSubmit();
                                    }
                                }}
                            >
                                Save
                            </SubmitButton>
                        )}
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getOverrideExperiences = (experiences: Experience[], catalogItem: CatalogItem) => {
    return experiences.filter(
        (experience) => experience.catalog?.items?.find((item) => item.parent?.id === catalogItem.id)?.overrideParent,
    );
};

const MAX_OVERRIDES_TO_SHOW = 5;

const OverrideExperiencesComponent = ({ overrideExperiences }: { overrideExperiences: Experience[] }) => {
    const remainingProducts = overrideExperiences.length - MAX_OVERRIDES_TO_SHOW;

    return (
        <>
            <p className="mt-2 mb-2 text-gray-darker">
                This change will not apply to the following products since they have product-level demographic
                overrides. To update the demographics for these products, you will need to repeat these changes for each
                of the overridden products:
            </p>

            {overrideExperiences.slice(0, MAX_OVERRIDES_TO_SHOW).map((experience) => (
                <div key={experience.id} className="flex items-center">
                    <Dot className="mr-2 bg-black" />
                    <NoTranslate>{experience.name}</NoTranslate>
                </div>
            ))}

            {overrideExperiences.length > MAX_OVERRIDES_TO_SHOW && (
                <div className="flex items-center">
                    <Tooltip
                        content={
                            <DemographicsExperienceList
                                experiences={overrideExperiences.slice(MAX_OVERRIDES_TO_SHOW)}
                            />
                        }
                    >
                        <div className="flex cursor-pointer items-center">
                            <Dot className="mr-2 bg-black" />
                            <span>
                                <var data-var="productCount">{remainingProducts}</var> more products
                            </span>
                        </div>
                    </Tooltip>
                </div>
            )}
        </>
    );
};

const DemographicsExperienceList = ({ experiences }: { experiences: Experience[] }) => {
    if (!experiences) {
        return null;
    }

    return (
        <ul className="space-y-1 p-2" data-testid="demographics-listing-label">
            {experiences.map((experience) => {
                return (
                    <li key={experience.id} className="no-translate">
                        {experience.name}
                    </li>
                );
            })}
        </ul>
    );
};
